import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Crown,
  X,
  Loader2,
  TrendingUp,
  Users,
  Clock,
  ChevronDown,
  Menu,
  Home,
  Video,
  Settings,
  LogOut,
  Gift,
  CreditCard,
  MessageSquare,
  MoreVertical,
} from "lucide-react";
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { ReferralModal } from './ReferralModal';
import { UploadModal } from './UploadModal';
import { useUser } from '../providers/UserProvider';
import { useMetrics } from '../providers/MetricsProvider';
import './Navbar.css';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/DropdownMenu";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/Avatar";
import { useNavigate, useLocation } from 'react-router-dom';
import { openFeedback } from '../utils';

// SocialsProgressBar Component
const SocialsProgressBar = () => {
  const { user } = useUser();
  const [platforms, setPlatforms] = useState([]);
  
  useEffect(() => {
    if (user?.platforms) {
      // Filter only processing platforms and add progress
      const processingPlatforms = user.platforms
        .filter(p => p.status === 'processing')
        .map(p => ({
          ...p,
          progress: Math.random() * 100, // This should come from your actual data
        }));
      setPlatforms(processingPlatforms);
    }
  }, [user]);

  if (platforms.length === 0) return null;

  const totalWidth = 100; // Total width percentage
  const platformWidth = totalWidth / platforms.length;

  return (
    <div className="flex w-full gap-1 h-2 bg-gray-100 rounded-full overflow-hidden">
      {platforms.map((platform, index) => {
        const progress = 100 - platform.progress; // Inverse progress for the animation effect
        return (
          <div
            key={platform.id}
            className="relative h-full transition-all duration-1000 ease-out"
            style={{ width: `${platformWidth}%` }}
          >
            <div
              className="absolute inset-0 bg-gray-200"
              style={{
                width: `${progress}%`,
                transition: 'width 0.5s ease-out',
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={`/icons/${platform.name.toLowerCase()}.svg`}
                alt={platform.name}
                className="h-4 w-4"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Quick Stats Component
const QuickStats = ({ theme = 'light' }) => {
  const { viewsChange, followersChange, bestPostingTime, loading } = useMetrics();
  
  if (loading) {
    return (
      <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center">
        <div className="flex items-center gap-2 text-sm">
          <TrendingUp className={cn(
            "h-4 w-4 animate-pulse",
            theme === 'light' ? "text-gray-300" : "text-gray-600"
          )} />
          <div className={cn(
            "h-4 w-16 animate-pulse rounded",
            theme === 'light' ? "bg-gray-200" : "bg-gray-700"
          )} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Users className={cn(
            "h-4 w-4 animate-pulse",
            theme === 'light' ? "text-gray-300" : "text-gray-600"
          )} />
          <div className={cn(
            "h-4 w-16 animate-pulse rounded",
            theme === 'light' ? "bg-gray-200" : "bg-gray-700"
          )} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className={cn(
            "h-4 w-4 animate-pulse",
            theme === 'light' ? "text-gray-300" : "text-gray-600"
          )} />
          <div className={cn(
            "h-4 w-24 animate-pulse rounded",
            theme === 'light' ? "bg-gray-200" : "bg-gray-700"
          )} />
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center">
      <div className="flex items-center gap-2 text-sm">
        <TrendingUp className="h-4 w-4 text-green-500" />
        <span className={cn(
          "font-medium",
          viewsChange >= 0 ? "text-green-600" : "text-red-600",
          theme === 'dark' && (viewsChange >= 0 ? "text-green-400" : "text-red-400")
        )}>
          {viewsChange >= 0 ? '+' : ''}{viewsChange}% views
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Users className="h-4 w-4 text-blue-500" />
        <span className={cn(
          "font-medium",
          followersChange >= 0 ? "text-blue-600" : "text-red-600",
          theme === 'dark' && (followersChange >= 0 ? "text-blue-400" : "text-red-400")
        )}>
          {followersChange >= 0 ? '+' : ''}{followersChange} followers
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Clock className="h-4 w-4 text-purple-500" />
        <span className={cn(
          "font-medium",
          theme === 'light' ? "text-purple-600" : "text-purple-400"
        )}>
          Best time: {bestPostingTime}
        </span>
      </div>
    </div>
  );
};

// ReferralBanner Component
const ReferralBanner = ({ onClose, theme = 'light' }) => {
  const navigate = useNavigate();
  return (
    <div className={cn(
      "relative px-4 py-2 text-sm text-center",
      "transition-all duration-300 ease-in-out",
      theme === 'light'
        ? "bg-gradient-to-r from-purple-50/80 via-pink-50/80 to-purple-50/80"
        : "bg-gradient-to-r from-purple-900/20 via-pink-900/20 to-purple-900/20"
    )}>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <span className={theme === 'light' ? "text-gray-600" : "text-gray-300"}>
          You were referred by a friend! Upgrade now and get
        </span>
        <span className={cn(
          "font-semibold animate-shimmer",
          theme === 'light' ? "text-purple-600" : "text-purple-400"
        )}>
          30% off for 3 months
                </span>
              <Button
          variant="link"
          size="sm"
          className={cn(
            "font-medium group transition-all duration-200",
            theme === 'light' ? "text-purple-600" : "text-purple-400"
          )}
          onClick={() => navigate('/upgrade')}
        >
          <span className="group-hover:underline">Upgrade now</span>
          <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </Button>
      </div>
              <Button
                variant="ghost"
                size="icon"
        className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2 transition-all duration-200",
          theme === 'light'
            ? "hover:bg-purple-100/50 text-gray-400 hover:text-gray-600"
            : "hover:bg-purple-900/50 text-gray-500 hover:text-gray-300"
        )}
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
  );
};

// Add a simple useIsMobile hook
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// UserMenu Component
const UserMenu = ({ user, theme = 'light', onReferral, onLogout, navigate }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    setOpen(false);
    if (onLogout) onLogout();
    else window.location.href = `${process.env.API_BASE_URL}/logout`;
  };

  const menuItems = [
    {
      icon: <Settings className="h-4 w-4" />,
      label: 'Connected Accounts',
      onClick: () => {
        setOpen(false);
        navigate('/settings');
      },
    },
    // Only show these on mobile
    ...(isMobile ? [
      {
        icon: <CreditCard className="h-4 w-4" />,
        label: 'Billing',
        onClick: () => {
          setOpen(false);
          navigate('/upgrade');
        },
      },
      {
        icon: <Gift className="h-4 w-4" />,
        label: 'Share & Earn',
        onClick: () => {
          setOpen(false);
          if (onReferral) onReferral();
        },
      },
      {
        icon: <MessageSquare className="h-4 w-4" />,
        label: 'Send Feedback',
        onClick: () => {
          setOpen(false);
          openFeedback();
        },
      },
    ] : []),
    {
      icon: <LogOut className="h-4 w-4" />,
      label: 'Log out',
      onClick: handleLogout,
      className: 'text-red-500 hover:text-red-600',
    },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative",
            theme === 'light'
              ? "hover:bg-gray-100/50"
              : "hover:bg-gray-800/50"
          )}
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className={cn(
              theme === 'light'
                ? "bg-gray-100 text-gray-600"
                : "bg-gray-800 text-gray-400"
            )}>
              {user?.name?.charAt(0) || user?.email?.charAt(0)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(
          "w-56",
          theme === 'light'
            ? "bg-white border-gray-200"
            : "bg-gray-800 border-gray-700"
        )}
      >
        <DropdownMenuLabel className={theme === 'light' ? "text-gray-700" : "text-gray-300"}>
          My Account
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={theme === 'light' ? "bg-gray-200" : "bg-gray-700"} />
        {menuItems.map((item, index) => (
          <DropdownMenuItem
            key={index}
            className={cn(
              "cursor-pointer flex items-center gap-2",
              item.className || (
                theme === 'light'
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-gray-300 hover:bg-gray-700"
              )
            )}
            onClick={item.onClick}
          >
            {item.icon}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Navbar = ({ theme = 'light' }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [hideReferralBanner, setHideReferralBanner] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isPremiumUser = user?.subscribed || false;
  const navbarRef = useRef(null);

  useEffect(() => {
    const updateNavbarHeight = () => {
      if (navbarRef.current) {
        const height = navbarRef.current.offsetHeight;
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', updateNavbarHeight);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hideReferralBanner, isPremiumUser]);

  const handleLogout = () => {
    window.location.href = `${process.env.API_BASE_URL}/logout`;
  };

  return (
    <div 
      ref={navbarRef} 
      className={cn(
        "fixed top-0 left-0 right-0 z-[100]",
        "transition-all duration-200 ease-in-out",
        isScrolled ? "navbar-sticky" : "navbar-enter",
        theme === 'light' ? "glass-light" : "glass-dark",
        "safe-area-top"
      )}
    >
      {user?.referred_by && !isPremiumUser && !hideReferralBanner && (
        <div className="animated-banner">
          <ReferralBanner
            onClose={() => setHideReferralBanner(true)}
            theme={theme}
          />
          <div className="glow-line" />
        </div>
      )}
      
      <div className={cn(
        "h-16 px-4",
        isScrolled && "shadow-sm"
      )}>
        <div className="h-full mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center flex-1">
            <div className="flex items-center gap-4">
              <h2 className={cn(
                "text-lg font-semibold cursor-pointer",
                "bg-clip-text text-transparent animate-gradient",
                theme === 'light'
                  ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"
                  : "bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400"
              )}
              onClick={() => navigate('/')}
              >
                Backup Videos
              </h2>
              <div className="hidden md:block">
                <QuickStats theme={theme} />
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 ml-auto">
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "flex items-center gap-2 group",
                  theme === 'light'
                    ? "border-gray-200 hover:bg-gray-50"
                    : "border-gray-700 hover:bg-gray-800"
                )}
                onClick={() => setShowReferralModal(true)}
              >
                <Gift className={cn(
                  "h-4 w-4 sparkle-icon",
                  theme === 'light' ? "text-purple-600" : "text-purple-400"
                )} />
                <span className={cn(
                  "hidden md:inline transition-colors duration-200",
                  theme === 'light' 
                    ? "text-gray-600 group-hover:text-purple-600" 
                    : "text-gray-300 group-hover:text-purple-400"
                )}>
                  Share & Earn
                </span>
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <UserMenu
              user={user}
              theme={theme}
              onReferral={() => setShowReferralModal(true)}
              onLogout={handleLogout}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
      <ReferralModal
        open={showReferralModal}
        onOpenChange={setShowReferralModal}
        referralCode={user?.referral_code}
        theme={theme}
      />
    </div>
  );
};

export default Navbar;